import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, Phone, X, Briefcase, User, Info, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchEpiscopalStaff } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';

interface Episcopal {
  name: string;
  slug: string;
  role: string;
  email?: string;
  phone_number?: string;
  photo?: string; // optional photo
  brief_info?: string;
  role_description?: string;
  status?: string;
  hierarchy_level?: number;
}
// --- Improved Modal with AnimatePresence ---
const Modal = ({ isOpen, onClose, staff }: { isOpen: boolean; onClose: () => void; staff: Episcopal | null }) => {
  if (!staff) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden relative z-[101]"
          >
            <button onClick={onClose} className="absolute top-6 right-6 p-2 rounded-full bg-slate-100 text-slate-500 hover:bg-amber-100 hover:text-amber-700 transition-colors">
              <X size={20} />
            </button>
            
            <div className="h-32 bg-slate-900 flex items-end px-8 pb-4">
               <div className="bg-amber-500 text-white p-3 rounded-2xl -mb-10 shadow-lg">
                  <Briefcase size={28} />
               </div>
            </div>

            <div className="p-8 pt-12">
              <h2 className="text-3xl font-serif font-bold text-slate-900 mb-1">{staff.name}</h2>
              <p className="text-amber-600 font-bold uppercase tracking-widest text-xs mb-6">{staff.role}</p>
              
              <div className="space-y-6">
                {staff.brief_info && (
                  <div className="flex gap-4">
                    <Info className="w-5 h-5 text-slate-300 shrink-0 mt-1" />
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400">Brief Overview</p>
                      <p className="text-slate-600 leading-relaxed">{staff.brief_info}</p>
                    </div>
                  </div>
                )}
                {staff.role_description && (
                  <div className="flex gap-4">
                    <ShieldCheck className="w-5 h-5 text-slate-300 shrink-0 mt-1" />
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400">Key Responsibilities</p>
                      <p className="text-slate-600 leading-relaxed">{staff.role_description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// --- Modern Staff Card ---
const StaffCard = ({ staff, onClick }: { staff: Episcopal; onClick: () => void }) => (
  <motion.div whileHover={{ y: -5 }} className="group">
    <Card 
      className="border-none shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer bg-white rounded-[2rem] overflow-hidden"
      onClick={onClick}
    >
      <div className="p-6 flex flex-col items-center text-center">
        <div className="relative mb-4">
          <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-100 ring-4 ring-slate-50 transition-all group-hover:ring-amber-100">
            {staff.photo ? (
              <img src={staff.photo} alt={staff.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center">
                <User className="text-white w-8 h-8 opacity-50" />
              </div>
            )}
          </div>
        </div>

        <h3 className="text-md font-serif font-bold text-slate-900 line-clamp-1">{staff.name}</h3>
        <p className="text-amber-600 text-[10px] font-bold uppercase tracking-tighter mb-4">{staff.role}</p>

        <div className="w-full pt-4 border-t border-slate-50 space-y-2">
          {staff.email && (
            <Link 
              to={`mailto:${staff.email}`} 
              className="flex items-center justify-center text-[11px] text-slate-500 hover:text-amber-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="h-3 w-3 mr-2" />
              <span className="truncate max-w-[140px]">{staff.email}</span>
            </Link>
          )}
        </div>
      </div>
    </Card>
  </motion.div>
);

const EpiscopalOffice = () => {
  const [loading, setLoading] = useState(true);
  const [episcopalStaff, setEpiscopalStaff] = useState<Episcopal[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<Episcopal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await fetchEpiscopalStaff();
        const data = response.data || [];
        setEpiscopalStaff(data.sort((a, b) => (a.hierarchy_level || 0) - (b.hierarchy_level || 0)));
      } catch (error) {
        // ... (fallback data as per your original logic)
        setEpiscopalStaff(fallbackData);
      } finally {
        setLoading(false);
      }
    };
    loadStaff();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fcfaf7] flex items-center justify-center">
        <div className="h-10 w-10 border-4 border-amber-600/20 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-20 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Top Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-100 mb-6">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Diocesan Secretariat</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 mb-4">Episcopal Office</h1>
          <p className="text-slate-500 font-serif italic text-lg">The Administrative Heart of Ibadan South</p>
        </div>

        {/* Leadership Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-24">
          <LeaderCard 
            title="The Lord Bishop"
            name="Rt. Rev. AkinTunde Popoola"
            quote="I am committed to leading this diocese with wisdom, compassion, and dedication to the Gospel of Jesus Christ."
            accent="amber"
          />
          <LeaderCard 
            title="Mama Ibadan South"
            name="Dr. Oyebola Popoola"
            quote="Supporting the ministry through women's programs, community outreach, and pastoral care for families."
            accent="purple"
          />
        </div>

        {/* Grid Section */}
        <div className="space-y-12">
          <div className="flex items-center gap-4">
             <div className="h-px bg-slate-200 grow" />
             <h2 className="text-2xl font-serif font-bold text-slate-800 shrink-0">Office Personnel</h2>
             <div className="h-px bg-slate-200 grow" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {episcopalStaff.map((staff, index) => (
              <StaffCard key={index} staff={staff} onClick={() => handleCardClick(staff)} />
            ))}
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} staff={selectedStaff} />
    </div>
  );
};

// --- Helper component for the two main leaders ---
const LeaderCard = ({ title, name, quote, accent }: { title: string, name: string, quote: string, accent: 'amber' | 'purple' }) => {
  const isAmber = accent === 'amber';
  return (
    <Card className="border-none shadow-sm bg-white rounded-[3rem] p-10 relative overflow-hidden group">
      <div className={`absolute top-0 right-0 w-32 h-32 ${isAmber ? 'bg-amber-50' : 'bg-purple-50'} rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110`} />
      <div className="relative z-10 flex flex-col items-center text-center">
        <div className={`w-24 h-24 rounded-[2rem] mb-6 flex items-center justify-center ${isAmber ? 'bg-amber-500 shadow-amber-200' : 'bg-purple-600 shadow-purple-200'} shadow-2xl`}>
          <User className="text-white w-10 h-10" />
        </div>
        <p className={`text-xs font-bold uppercase tracking-[0.3em] mb-2 ${isAmber ? 'text-amber-600' : 'text-purple-600'}`}>{title}</p>
        <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4">{name}</h3>
        <p className="text-slate-500 font-serif italic leading-relaxed px-4">"{quote}"</p>
      </div>
    </Card>
  );
};

export default EpiscopalOffice;